import { useSelector } from 'react-redux';
import styles from './Filter.module.scss';
import Button from '../../atoms/button/Button';
import { Checkbox } from '../../atoms/checkbox/Checkbox';
import { convertTextToId } from '@srcDS/js/utils/convertTextToId';
import { useFilter } from '@srcDS/tastic/pages/category/hooks/useFilter';
import CustomRange from '../customRange/CustomRange';
import { useMedia } from 'react-use';
import MobileFilters from './MobileFilters';
import { useCategoryContext } from '../../templates/category/context/useCategoryContext';
import { useMemo } from 'react';
import { sortedFacetsTerm } from './utils/sortedFacetsTerm';
import { 
  Term, 
  FilterFacet, 
  LocalFilterFacet, 
  FilterProps, 
  RootState, 
  UseFilterReturn,
  FilterDisplayValues
} from './types';

export type { Term, FilterFacet } from './types';

export const Filter: React.FC<FilterProps> = ({
  filters = [],
  isGlobalLoaderShow,
  triggerRoute,
  setFilterDisplayValues,
}) => {
  const isMobile = useMedia('(max-width: 1000px)', false);
  const locale = useSelector((state: RootState) => state.app.context.locale);
  const rangeFilter = filters.find((filter) => filter.attributeId === 'variants.price');

  const { handleClearAllFacets, handleFacetChange, onPriceRangeChange, handleSubmitFaceChange }: UseFilterReturn =
    useFilter();

  // @ts-ignore
  const { localFilters } = useCategoryContext();

  const filterCount: number = useMemo((): number => {
    return (
      (Object.values(localFilters) as LocalFilterFacet[]).reduce((acc: number, filter: LocalFilterFacet) => {
        if (filter.type === 'term' || filter.type === 'boolean') {
          return acc + (filter.terms?.filter((term) => term.selected).length || 0);
        }

        if (Array.isArray(filter) && filter?.[0].type === 'range') {
          return acc + 1;
        }

        return acc;
      }, 0) ?? 0
    );
  }, [localFilters]);

  const getFacetTitle = (filter: FilterFacet): string => {
    if (filter.attributeId === 'variants.attributes.primarycategory') {
      return 'Category';
    }
    return filter?.label[locale] || filter?.label['en_AU@AUD'] || 'No luck';
  };

  const renderRange = (filter: FilterFacet): JSX.Element => {
    return <CustomRange key={`${filter.max}-${filter.min}`} facet={filter} onPriceRangeChange={onPriceRangeChange} />;
  };

  const renderFilter = (filter: FilterFacet): JSX.Element | null => {
    if (!['range', 'term', 'boolean'].includes(filter.type)) {
      return null;
    }
    if (filter.type === 'term' && (!filter.terms || filter.terms.length === 0)) return null;
    if (filter.type === 'range' && !(filter.max || filter.min)) return null;

    const termsToRender: Term[] = localFilters[filter.attributeId]?.terms || filter.terms || [];

    return (
      <div className={styles.filterContainer} style={filter.type === 'range' ? { marginRight: 100 } : undefined}>
        <p className={styles.filterTitle}>{getFacetTitle(filter)}</p>
        {filter.type === 'term' || filter.type === 'boolean'
          ? sortedFacetsTerm(filter.attributeId, termsToRender)?.map((it: Term, termIndex: number) => {
              const isSelected = localFilters[filter.attributeId]?.terms
                ? (localFilters[filter.attributeId].terms.find((t) => t.value === it.value)?.selected ?? false)
                : it.selected;

              return (
                !!it.count && (
                  <div key={`${filter.attributeId}_${termIndex}`} className={styles.filtersList}>
                    <div className={styles.filterItems}>
                      {/* @ts-ignore */}
                      <Checkbox
                        checkStatus={isSelected}
                        customLabelStyle={styles.filterItemsLabel}
                        onChange={(status: boolean) => {
                          handleFacetChange(filter, status, termIndex, filter.attributeId);
                        }}
                        className={styles.checkbox}
                        dataAttributes={{
                          'data-filter-attribute': convertTextToId(it.value),
                        }}
                      >
                        {it?.isColor && (
                          <div
                            className={styles.colorIndicator}
                            style={{
                              background:
                                it.value.toLowerCase() === 'multi'
                                  ? 'conic-gradient(red, orange, yellow, green, blue, purple, red)'
                                  : it.value.toLowerCase() === 'cream'
                                    ? '#F5DFBD'
                                    : it.value.toLowerCase(),
                            }}
                          />
                        )}
                        <span>{it.value}</span>
                      </Checkbox>
                    </div>
                  </div>
                )
              );
            })
          : filter.type === 'range'
            ? renderRange(filter)
            : null}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {isMobile ? (
        <MobileFilters facets={filters} isGlobalLoaderShow={isGlobalLoaderShow} triggerRoute={triggerRoute} />
      ) : (
        <div className={styles.filters}>{filters.map(renderFilter)}</div>
      )}

      <div className={styles.buttonsContainer}>
        {/* @ts-ignore */}
        <Button
          variant="primaryOutlined"
          customClasses={{
            button: styles.filterButton,
          }}
          onClick={handleClearAllFacets}
          disabled={Object.keys(localFilters).length === 0 && !rangeFilter?.selected}
        >
          Clear Filters
        </Button>
        {/* @ts-ignore */}
        <Button
          variant="primary"
          customClasses={{
            button: styles.filterButton,
          }}
          onClick={() => {
            handleSubmitFaceChange({
              callback: () => {
                setFilterDisplayValues((values: FilterDisplayValues) => ({
                  ...values,
                  filterShown: false,
                }));
              },
            });
          }}
        >
          <span>Apply Filters {filterCount > 0 && `(${filterCount})`}</span>
        </Button>
      </div>
    </div>
  );
};
